// We're using * imports because we're using commonjs and ts-node to download these.
import * as Prismic from "prismic-javascript";
import Cookies from "js-cookie";
import { fetchLinkLookup, CmsDocumentTypeMap, CmsDocument } from "./cms-type-definitions";
import ICmsStore from "./i-cms-store";

const PrismicConfig = {
  apiEndpoint: "https://characterscope.cdn.prismic.io/api/v2",
  accessToken:
    "MC5Xb2NZUVNjQUFPZWoxZXJx.RO-_vUcM77-9Cu-_ve-_ve-_vV5b77-9XO-_vUdrC--_ve-_ve-_ve-_vT_vv73vv73vv70m77-9FSHvv70iCw",
};

export default class CmsRemoteStore implements ICmsStore {
  static api = Prismic.getApi(PrismicConfig.apiEndpoint, {
    accessToken: PrismicConfig.accessToken,
  });

  private static cachedFetchLinks: string | string[] | null = null;

  private get fetchLinks(): string | string[] {
    if (!CmsRemoteStore.cachedFetchLinks) {
      const list = [] as string[];

      Object.keys(fetchLinkLookup).forEach((cmsType) => {
        fetchLinkLookup[cmsType].forEach((member) => {
          list.push(`${cmsType}.${member}`);
        });
      });

      CmsRemoteStore.cachedFetchLinks = list;
    }

    return CmsRemoteStore.cachedFetchLinks;
  }

  private async ref(): Promise<string> {
    const previewRef = Cookies.get(Prismic.previewCookie);
    const masterRef = (await CmsRemoteStore.api).refs.find((ref) => {
      return ref.isMasterRef === true;
    });
    const ref = previewRef || masterRef?.ref;
    return ref;
  }

  async getById(id: string): Promise<CmsDocument> {
    // https://prismic.io/docs/php/query-the-api/query-options-reference
    const result = await (
      await CmsRemoteStore.api
    ).getByID(id, { fetchLinks: this.fetchLinks, ref: await this.ref() });
    if (!result) {
      throw new Error(`getById("${id}") did not find a document with that id`);
    }

    // https://github.com/prismicio/prismic-javascript/issues/30
    // Bugfix workaround for type definition files.
    return result as CmsDocument;
  }

  async getByUid(type: string, uid: string): Promise<CmsDocument> {
    if (typeof type !== "string" || typeof uid !== "string" || !uid || !type) {
      throw new Error(
        `CmsRemoteStore.getByUid(${JSON.stringify(type)}, ${JSON.stringify(
          uid,
        )}) has bad arguments`,
      );
    }

    // https://prismic.io/docs/php/query-the-api/query-options-reference
    const result = await (
      await CmsRemoteStore.api
    ).getByUID(type, uid, { fetchLinks: this.fetchLinks, ref: await this.ref() });
    if (!result) {
      throw new Error(
        `CmsRemoteStore.getByUid(${JSON.stringify(type)}, ${JSON.stringify(
          uid,
        )}) did not find a document with that uid`,
      );
    }

    // https://github.com/prismicio/prismic-javascript/issues/30
    // Bugfix workaround for type definition files.
    return result as CmsDocument;
  }

  async getAll<X extends keyof CmsDocumentTypeMap>(type: X): Promise<CmsDocumentTypeMap[X][]> {
    const api = await CmsRemoteStore.api;
    const getPage = async (page: number) =>
      api.query(Prismic.Predicates.at("document.type", type), {
        fetchLinks: this.fetchLinks,
        page,
        ref: await this.ref(),
      });
    const response = await getPage(1);
    let { results } = response;

    for (let page = 2; page <= response.total_pages; page++) {
      // Can't just 'await all' as the results need to be in order - TODO: map/reduce this
      // eslint-disable-next-line no-await-in-loop
      results = results.concat((await getPage(page)).results);
    }

    // https://github.com/prismicio/prismic-javascript/issues/30
    // Bugfix workaround for type definition files.
    return results as any as CmsDocumentTypeMap[X][];
  }
}
